/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { AuthService, IAuthStatusSearchFilters } from 'app/um/services/auth-service';
import { I18N } from 'aurelia-i18n';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveCareDayStatus extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ changeHandler: 'reset' })
    public careDayStatus: UM.AdmissionCareDayStatus = {};

    protected selectedTransitions: UM.AdmissionCareDayStatus[] = [];
    protected addDetermination = false;
    protected isInitialStatus = false;

    // Dropdown Lookups
    protected transitions: UM.AdmissionCareDayStatus[] = [];
    protected determinationStatuses: UM.AuthStatus[] = [];

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly authService: AuthService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadLookups();
    }

    protected buildCareDayStatus(): UM.AdmissionCareDayStatus {
        const saveCareDayStatus: UM.AdmissionCareDayStatus = {
            id: this.careDayStatus.id,
            description: this.careDayStatus.description,
            code: forceNull(this.careDayStatus.code),
            authStatus: this.addDetermination ? getReference(this.careDayStatus.authStatus) : null,
            flag: {
                isTurnAroundTimeBase: this.careDayStatus.flag?.isTurnAroundTimeBase || false,
                allowStatusDateEdit: this.careDayStatus.flag?.allowStatusDateEdit || false
            },
            transitions: getReferences(this.selectedTransitions)
        };

        return saveCareDayStatus;
    }

    ////****************** IForm ****************************************************////
    public async reset(): Promise<void> {
        this.selectedTransitions = this.careDayStatus.transitions ? deepClone(this.careDayStatus.transitions) : [];
        this.addDetermination = this.careDayStatus.authStatus?.determination?.isDetermination || false;
        this.isInitialStatus = this.careDayStatus.flag?.isInitialStatus || false;
        await this.reload();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return this.saveCareDayStatus();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveCareDayStatus(): Promise<boolean> {
        let allSuccessful = false;
        let invalidCode = false;
        let invalidInitialStatus = false;
        try {
            const saveCareDayStatus: UM.AdmissionCareDayStatus = this.buildCareDayStatus();
            // throw error if user is attempting to deselect Initial Status
            if(this.isInitialStatus && !this.careDayStatus.flag?.isInitialStatus) {
                throw { name: 'CannotDeselectIsInitialStatus' };
            }

            const saveResponse = await this.service.saveCareDayStatus(saveCareDayStatus);

            // if Initial Status is true, call !updateExclusive action on this ID
            const careDayStatusId = saveCareDayStatus.id || saveResponse.id;
            if(this.careDayStatus.flag?.isInitialStatus && careDayStatusId) {
                const updateExclusiveResponse = await this.service.updateCareDayStatusExclusive(careDayStatusId);
                allSuccessful = saveResponse.isSuccessful && updateExclusiveResponse.isSuccessful;
            } else {
                allSuccessful = saveResponse.isSuccessful;
            }
        }
        catch(error) {
            // If user has attempted to deselect Initial Status checkbox
            invalidInitialStatus = error.name === 'CannotDeselectIsInitialStatus';
            // If code constraint is violated
            if(error.status.errors) {
                invalidCode = error.status.errors.find((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
            }
            throw error;
        }
        finally {
            if(invalidCode) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:auth.careDays.status.code.$long'), item: this.i18n.tr('um:auth.careDays.status.$long') })).show();
            } else if(invalidInitialStatus) {
                this.toaster.error().message(this.i18n.tr('um:local.careDayStatus.careDayStatusEventChangeError')).show();
            } else {
                this.toaster.standard.saved('um:auth.careDays.status.$long', allSuccessful);
            }
            return allSuccessful;
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const options: Partial<IQueryLoadOptions> = { all: true };
            const searchFilters: IAuthStatusSearchFilters = { isDetermination: true };

            const [transitions, determinationStatuses] = await Promise.all([
                await this.service.loadCareDayStatuses(options),
                await this.authService.loadAuthStatuses(options, [], searchFilters)
            ]);

            this.transitions = transitions.results;
            this.determinationStatuses = determinationStatuses.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
